import axios from "axios";

import i18n from "@/i18n.js";
import store from "@/store";
import { capitalize } from "@/functions";

/**
 * SGE access control.
 * The very same allowlist is enforced by raijin (api/sge/*) and by sge_m2m_api
 * (SGE_ALLOWED_EMAILS). Keep the three lists in sync.
 */
export const SGE_ALLOWED_EMAIL = [
  "gberriot@innovent.fr",
  "acabot@innovent.fr",
  "qmoulier@innovent.fr",
  "jmoussu@innovent.fr",
  "jhammerchmidt@innovent.fr",
  "tgosselin@innovent.fr",
  "msadallah@innovent.fr",
  "jpnoyelle@innovent.fr",
  "mourmant@innovent.fr",
];

export function isSgeUser(user) {
  const email = user?.email?.toLowerCase();
  return !!email && SGE_ALLOWED_EMAIL.includes(email);
}

/**
 * Power target sent with the order, in the SGE sign convention (production allowed is negative):
 * Stop  -> -limitKw (0 by default = full stop). The API raises it to the site's p_min when lower.
 * Start -> -20000 (full release).
 */
export function sgeTargetFor(sgeOrder, limitKw = 0) {
  if (!sgeOrder) return -20000;
  const kw = Math.abs(Number(limitKw) || 0);
  return kw === 0 ? 0 : -kw;
}

/** Target actually applied by the API, parsed from its "SGE_TARGET_skW=..." reply (kW, positive). */
export function parseAppliedKw(text) {
  const m = /SGE_TARGET_skW=(-?\d+(?:\.\d+)?)/.exec(String(text || ""));
  return m ? Math.abs(Number(m[1])) : null;
}

/**
 * Axios client talking directly to sge_m2m_api.
 * Authentication: the user's raijin JWT as Bearer token; sge_m2m_api verifies it
 * against raijin and applies the SGE allowlist. No shared secret lives in the frontend.
 */
// Override with VUE_APP_SGE_API_URL in .env (e.g. a local sge_m2m_api on http://localhost:8000).
const SGE_API_URL = process.env.VUE_APP_SGE_API_URL || "https://sge.innovent.fr";

export const sgeApi = axios.create({
  baseURL: SGE_API_URL.replace(/\/+$/, ""),
  timeout: 90000,
});

sgeApi.interceptors.request.use((config) => {
  const token = localStorage.getItem("token");
  if (token !== null) {
    config.headers["Authorization"] = `Bearer ${token}`;
  }
  return config;
});

sgeApi.interceptors.response.use(undefined, (error) => {
  let message;
  if (error.response !== undefined) {
    message = capitalize(
      i18n.global.t("error.default", { spec: error.response.statusText })
    );
    const data = error.response.data;
    if (data !== undefined && data !== null) {
      if (typeof data === "string" && data.length < 300) {
        message = data;
      } else if (data.detail !== undefined) {
        message = typeof data.detail === "string" ? data.detail : JSON.stringify(data.detail);
      } else if (data.message !== undefined) {
        message = data.message;
      }
    }
  } else if (error.code === "ECONNABORTED") {
    message = capitalize(i18n.global.t("sge.timeout"));
  } else {
    message = capitalize(i18n.global.t("error.unknown"));
  }
  store.dispatch("snackbar/showError", message);
  return Promise.reject(error);
});

/** Live status of every active SGE producer. */
export function fetchSgeProducers() {
  return sgeApi.get("/sge/producers", { timeout: 60000 }).then((r) => r.data);
}

/**
 * Send one SGE order to one site. Resolves to a result row, never rejects,
 * so a batch can report per-site outcomes.
 */
export async function sendSgeSetPoint(siteId, sgeOrder, instant = false, limitKw = 0) {
  const t0 = performance.now();
  try {
    const resp = await sgeApi.post(
      // Fujin-only endpoint (raijin JWT): exposes the `instant` option.
      `/${encodeURIComponent(siteId)}/sge/fujin/setPoint`,
      null,
      {
        params: {
          sge_order: sgeOrder,
          sge_target_skW: sgeTargetFor(sgeOrder, limitKw),
          // Stop: true = the PLC applies at once; false = temporary phase first (instant after 10 min).
          // Start is always instant.
          instant: sgeOrder ? Boolean(instant) : true,
        },
        // Each call is reported individually in the result dialog: silence the global snackbar.
        validateStatus: () => true,
      }
    );
    return {
      site_id: siteId,
      status_code: resp.status,
      ok: resp.status >= 200 && resp.status < 300,
      applied_kw: typeof resp.data === "string" ? parseAppliedKw(resp.data) : null,
      response: typeof resp.data === "string" ? resp.data.slice(0, 200) : JSON.stringify(resp.data).slice(0, 200),
      elapsed_ms: Math.round(performance.now() - t0),
    };
  } catch (err) {
    return {
      site_id: siteId,
      status_code: null,
      ok: false,
      response: err?.code === "ECONNABORTED" ? "Timeout" : String(err?.message || err),
      elapsed_ms: Math.round(performance.now() - t0),
    };
  }
}
